import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Switch } from "@shape-ds/components";
import { dayjs } from "../../../lib";
import { SelectByCategory } from "../../SelectByCategory";
import { DateFilters } from "../../DateFilters/DateFilters";
import { Attribute } from "../../../utils/types";
import { getDateRange } from "../../../utils/dateRangeUtils";
import { TIME_FORMAT_WITH_UTC } from "../../../config/constants";
import { t } from "i18next";
import { useChartVariables } from "../hooks/Chart.context";

export const defaultChartPeriodOptions = () => [
  {
    label: t("components:date_filter.last_n_hours", { hours: 6 }),
    value: "6",
  },
  {
    label: t("components:date_filter.last_n_hours", { hours: 24 }),
    value: "24",
  },
  {
    label: t("components:date_filter.last_n_days", { days: 7 }),
    value: "168",
  },
  {
    label: t("components:date_filter.last_n_days", { days: 30 }),
    value: "720",
  },
  {
    label: t('components:date_filter.last_n_days', { days: 90 }),
    value: '2160',
  },
  {
    label: t('components:date_filter.last_n_days', { days: 365 }),
    value: '8760',
  },
  {
    label: t('components:date_filter.last_n_days', { days: 730 }),
    value: '17520',
  },
];

const ChartFilters: React.FC<{
  enableCustomRange?: boolean;
  dateFormat?: string;
  showExcludeGap?: boolean;
  children?: React.ReactNode;
  hasAlarmPeriod?: boolean;
  hasLegendCategory?: boolean;
  tags?: { [key: string]: ChartTag[] | Attribute[] };
  customPeriodOptions?: {
    label: string;
    value: string;
  }[];
  maxMonthsRange?: number;
  modelName?: string;
}> = ({
  showExcludeGap,
  children,
  enableCustomRange,
  dateFormat,
  hasAlarmPeriod,
  hasLegendCategory,
  tags,
  customPeriodOptions,
  maxMonthsRange,
  modelName,
}) => {
  const { t } = useTranslation();
  const {
    excludeGap,
    setExcludeGap,
    changeDateRange,
    dateRange,
    period,
    defaultPeriod,
    legendCategory,
    setLegendCategory,
  } = useChartVariables();

  const onClearCalendar = () => {
    changeDateRange({ period: defaultPeriod });
  };

  useEffect(() => {
    changeDateRange({
      period: defaultPeriod,
      dateRange: getDateRange(defaultPeriod),
    });
  }, [modelName]);

  const periodOptions = hasAlarmPeriod
    ? [
        {
          label: t("components:date_filter.alarm"),
          value: "alarm",
        },
        ...(customPeriodOptions ?? defaultChartPeriodOptions()),
      ]
    : customPeriodOptions ?? defaultChartPeriodOptions();

  return (
    <div className="line-chart-filters">
      {children}
      {showExcludeGap && (
        <Switch
          label={t(`monitoring:charts.labels.switch`)}
          labelPosition="start"
          checked={excludeGap}
          onChange={() => setExcludeGap(!excludeGap)}
        />
      )}
      {hasLegendCategory && tags && (
        <SelectByCategory
          setValue={(value) => setLegendCategory(value)}
          value={legendCategory}
          fullWidth
          tags={tags}
        />
      )}

      <DateFilters
        className="chart-filters-date"
        enableCalendar
        periodOptions={periodOptions}
        defaultPeriod={defaultPeriod}
        enableCustomPeriod={enableCustomRange}
        dateFormat={dateFormat ?? TIME_FORMAT_WITH_UTC}
        handlePeriodChange={(period) => {
          changeDateRange({ period });
        }}
        handleCalendarChange={(dateRange) =>
          changeDateRange({
            period: "custom",
            dateRange: {
              end: dayjs(dateRange.endDate),
              start: dayjs(dateRange.startDate),
            },
          })
        }
        handleClear={onClearCalendar}
        defaultDateRange={
          period === "custom" && dateRange.start
            ? [dateRange.start, dateRange.end]
            : undefined
        }
        maxMonthsRange={maxMonthsRange}
        modelName={modelName}
      />
    </div>
  );
};

export default ChartFilters;
